// R217.0A propagation safety net.
// Polls /version.json on a low-frequency cadence (and on tab focus). If the
// deployed build number diverges from the build the user loaded, surfaces a
// non-blocking banner with a Refresh button. Never auto-reloads — the user
// chooses when to interrupt their flow. Refresh hits index.html (no-cache per
// vercel.json) which pulls the fresh chunk hashes.
import React, { useEffect, useRef, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  requestWaitingServiceWorkerActivation,
  serviceWorkerUpdateEvent,
} from '../serviceWorkerRegistration';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min — light enough to ignore.
const VERSION_URL = '/version.json';

async function fetchBuild() {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return Number(data?.build) || null;
  } catch {
    return null;
  }
}

export default function UpdateAvailableBanner() {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isMobile = useMediaQuery('(max-width:899.95px)');
  const baselineRef = useRef(null);
  const latestRef = useRef(null);
  const liveRegionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (navigator.serviceWorker?.controller) return;
      const b = await fetchBuild();
      if (cancelled || b == null) return;
      if (baselineRef.current == null) {
        baselineRef.current = b;
        return;
      }
      latestRef.current = b;
      if (b > baselineRef.current) setAvailable(true);
    };

    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, []);

  useEffect(() => {
    const onServiceWorkerUpdate = () => setAvailable(true);
    window.addEventListener(serviceWorkerUpdateEvent, onServiceWorkerUpdate);
    return () => window.removeEventListener(serviceWorkerUpdateEvent, onServiceWorkerUpdate);
  }, []);

  useEffect(() => {
    if (!liveRegionRef.current) return;
    liveRegionRef.current.textContent = available && !dismissed
      ? 'A new EliteEdge version is available. Refresh when ready.'
      : '';
  }, [available, dismissed]);

  const refresh = async () => {
    let completed = false;
    const reload = () => {
      if (completed) return;
      completed = true;
      window.location.reload();
    };
    const timeoutId = window.setTimeout(reload, 5000);
    navigator.serviceWorker?.addEventListener?.('controllerchange', () => {
      window.clearTimeout(timeoutId);
      reload();
    }, { once: true });

    const requested = await requestWaitingServiceWorkerActivation();
    if (!requested) {
      window.clearTimeout(timeoutId);
      reload();
    }
  };

  return (
    <>
      <div
        ref={liveRegionRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      {available && !dismissed && (
        <div
          data-testid="update-available-banner"
          role="region"
          aria-label="EliteEdge update available"
          style={{
            position: 'fixed',
            bottom: isMobile
              ? 'calc(80px + env(safe-area-inset-bottom, 0px))'
              : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#0f172a',
            color: '#e2e8f0',
            border: '1px solid #2DD4BF',
            borderRadius: 10,
            padding: '10px 14px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            maxWidth: 'calc(100vw - 32px)',
            fontSize: 14,
          }}
        >
          <span>App update ready - tap Refresh to load it</span>
          <button
            type="button"
            data-testid="update-available-refresh"
            onClick={refresh}
            style={{
              background: 'var(--accent)',
              color: '#0b1220',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontWeight: 700,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            Refresh
          </button>
          <button
            type="button"
            aria-label="Dismiss update notification"
            onClick={() => setDismissed(true)}
            style={{
              background: 'transparent',
              color: '#94a3b8',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              padding: '4px 8px',
              minHeight: 44,
              minWidth: 44,
            }}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      )}
    </>
  );
}
